import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import {
  DD_RUM_APPLICATION_ID,
  DD_RUM_CLIENT_TOKEN,
  DD_ENV,
} from '../constants/datadog';

const API_DOMAIN = import.meta.env.VITE_MEDUSA_API_URL;

let isRumInitialized = false;
let sessionId: string | null = null;

export const initDatadogRUM = () => {
  if (isRumInitialized) {
    console.log('Datadog RUM already initialized, skipping...');
    return;
  }

  try {
    datadogRum.init({
      applicationId: DD_RUM_APPLICATION_ID,
      clientToken: DD_RUM_CLIENT_TOKEN,
      site: 'us5.datadoghq.com',
      service: 'medusa-admin-ui',
      env: DD_ENV,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: (event) => {
        if (
          event.type === 'resource' &&
          event.resource?.type === 'xhr' &&
          event.resource?.url &&
          API_DOMAIN &&
          !event.resource.url.includes(API_DOMAIN)
        ) {
          return false;
        }
        if (
          event.type === 'resource' &&
          event.resource?.type === 'xhr' &&
          API_DOMAIN &&
          !event.resource?.url
        ) {
          return false;
        }
        // Drop all other resource events that are not XHR/fetch to API_DOMAIN
        if (
          event.type === 'resource' &&
          event.resource?.type !== 'xhr' &&
          event.resource?.type !== 'fetch'
        ) {
          return false;
        }
        return true;
      },
    });

    isRumInitialized = true;
    console.log('Datadog RUM initialized successfully');

    datadogRum.onReady(() => {
      console.log('Datadog RUM is ready');
      try {
        const context = datadogRum.getInternalContext();
        console.log('RUM context when ready:', context);
        if (context?.session_id) {
          sessionId = String(context.session_id);
          console.log('Session ID captured:', sessionId);
        }
      } catch (error) {
        console.error('Error getting session ID when ready:', error);
      }
    });

    setTimeout(() => {
      try {
        const context = datadogRum.getInternalContext();
        console.log('RUM context after delay:', context);
        if (context?.session_id && !sessionId) {
          sessionId = String(context.session_id);
          console.log('Session ID captured after delay:', sessionId);
        }
      } catch (error) {
        console.error('Error getting session ID after delay:', error);
      }
    }, 2000);
  } catch (error) {
    console.error('Error initializing Datadog RUM:', error);
  }
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  pillexa_role?: string;
  first_name?: string;
  last_name?: string;
  department?: string;
  phone?: string;
  [key: string]: any;
}) => {
  const { id, email, pillexa_role, first_name, last_name, department, phone, ...customFields } = user;
  datadogRum.setUser({
    id,
    email,
    name: [first_name, last_name].filter(Boolean).join(' '),
    plan: pillexa_role,
    department,
    phone,
    ...customFields,
  });
};

export const setSessionContext = (sessionId: string) => {
  datadogRum.addAction('session_start', {
    session_id: sessionId,
  });
};

export const trackFeature = (featureName: string, actionType: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('feature_usage', {
    feature_name: featureName,
    action_type: actionType,
    ...metadata,
  });
};

export const trackOrderAction = (actionType: string, orderId: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('order_action', {
    action_type: actionType,
    order_id: orderId,
    ...metadata,
  });
};

export const trackTicketAction = (actionType: string, ticketId: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('ticket_action', {
    action_type: actionType,
    ticket_id: ticketId,
    ...metadata,
  });
};

export const trackCustomerAction = (actionType: string, customerId: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('customer_action', {
    action_type: actionType,
    customer_id: customerId,
    ...metadata,
  });
};

export const trackMacroAction = (actionType: string, macroId: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('macro_action', {
    action_type: actionType,
    macro_id: macroId,
    ...metadata,
  });
};

export const trackApiCall = (
  endpoint: string,
  method: string,
  statusCode: number,
  duration: number,
  metadata?: Record<string, any>,
) => {
  datadogRum.addAction('api_call', {
    endpoint,
    method,
    status_code: statusCode,
    duration,
    ...metadata,
  });
};

export const trackPageView = (pageName: string, metadata?: Record<string, any>) => {
  datadogRum.addAction('page_view', {
    page_name: pageName,
    ...metadata,
  });
};

export const trackError = (error: Error, context?: Record<string, any>) => {
  datadogRum.addError(error, {
    ...context,
  });
};

export const getSessionId = (): string => {
  try {
    if (sessionId) {
      console.log('Using stored session ID:', sessionId);
      return sessionId;
    }

    const windowRum = (window as any).DD_RUM;
    if (windowRum) {
      console.log('Found DD_RUM on window object:', windowRum);

      if (windowRum.getInternalContext) {
        const context = windowRum.getInternalContext();
        console.log('Window DD_RUM internal context:', context);
        if (context?.session_id) {
          sessionId = String(context.session_id);
          return sessionId;
        }
      }

      if (windowRum.session_id) {
        sessionId = String(windowRum.session_id);
        return sessionId;
      }

      if (windowRum.getRumGlobalContext) {
        const globalContext = windowRum.getRumGlobalContext();
        if (globalContext?.session_id) {
          sessionId = String(globalContext.session_id);
          return sessionId;
        }
      }

      if (windowRum.internalContext?.session_id) {
        sessionId = String(windowRum.internalContext.session_id);
        return sessionId;
      }
    }

    try {
      const context = datadogRum.getInternalContext();
      console.log('Datadog RUM internal context:', context);
      if (context?.session_id) {
        sessionId = String(context.session_id);
        return sessionId;
      }
    } catch (error) {
      console.log('Error getting context from datadogRum import:', error);
    }

    try {
      const storedSessionId = localStorage.getItem('dd_session_id');
      if (storedSessionId) {
        sessionId = storedSessionId;
        console.log('Found session ID in localStorage:', sessionId);
        return sessionId;
      }
    } catch (error) {
      console.log('Error checking localStorage:', error);
    }

    console.warn('Could not retrieve session ID from Datadog RUM, generating fallback');
    const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionId = fallbackId;
    return fallbackId;
  } catch (error) {
    console.error('Error getting session ID:', error);
    const errorId = `error-${Date.now()}`;
    sessionId = errorId;
    return errorId;
  }
};

export const getUserId = (): string => {
  try {
    const user = datadogRum.getUser();
    return user?.id?.toString() || 'unknown';
  } catch {
    return 'unknown';
  }
};

export const addGlobalContext = (key: string, value: string | number | boolean) => {
  try {
    (datadogRum as any).addRumGlobalContext(key, value);
  } catch (error) {
    console.warn('Failed to add global context:', error);
  }
};

export const removeGlobalContext = (key: string) => {
  try {
    (datadogRum as any).removeRumGlobalContext(key);
  } catch (error) {
    console.warn('Failed to remove global context:', error);
  }
};

export const trackFeatureFlags = (flags: Record<string, boolean>) => {
  Object.entries(flags).forEach(([flag, value]) => {
    datadogRum.addFeatureFlagEvaluation(flag, value ? 'on' : 'off');
  });
};
